import { tl } from 'common/services/i18nService';
import {
    Confirm, Form, Header, Icon, Label, List, ListItem, Loader, Message, TextArea,
} from 'semantic-ui-react';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LabelSpinner from 'common/components/labelSpinner/LabelSpinner';
import styles from 'common/components/header/CommonSubHeader.scss';
import { isNullOrUndefined } from 'common/util/type/typeUtil';
import {
    MenuItem,
    MenuButton,
    MenuHeader,
    SubMenu,
    MenuDivider,
    MenuGroup,
    ClickEvent,
    ControlledMenu,
} from '@szhsin/react-menu';
import moment from 'moment';
import { SIMPLE_DATE_FORMAT_STRING_NO_SECONDS } from 'common/store/common/constants';
import '@szhsin/react-menu/dist/index.css';

import { supportArticleForExplanations } from 'common/services/api/apiConstants';
import {
    getOptionsMenuItems, MenuItemsGroup,
    MenuItemAction,
    OptionsMenuSubtitles,
} from '../../../services/optionsMenuItemsService';
import { RootState } from '../../../store/rootReducer';
import {
    onCancelAction,
    onCloseOptionsMenu,
    onFileReadyForUpload,
    onMenuItemClick, onOpenOptionsMenu,
    onToggleConfirmModal,
} from '../../../store/optionsMenu/actions';
import { OptionsMenuActionsName } from '../../../store/optionsMenu/types';
import {
    getCommitteeNameByID,
    getEvaluatorsNames,
    getNumberOfEvaluators,
    getCommitteeById,
} from '../../../services/committeeService';
import { getEvaluatorNameById } from '../../../services/setMarksServices';
import { isGraderThatStillHasNotGradedCandidates } from '../../../services/workflowService';
import { openNewTabInPlatform } from '../../../services/platformIAService';
import { onModerationActionClick } from '../../../store/assessment/actions';
import { ModerationPrimaryActions, ModerationStatus } from '../../../store/context/types';

const OptionsMenuButton = React.memo(() => {
    const optionsMenuRef = useRef(null);
    const [moderationComment, setModerationComment] = useState('');
    const {
        context, optionsMenu, assessment, session,
    } = useSelector((rootState: RootState) => rootState);
    const { selectedCommitteeId } = context;
    const dispatch = useDispatch();
    const {
        isProcessing, activeActionName, isConfirmModalOpen, markSchemeFile, isMenuOpen,
    } = optionsMenu;

    const handleOnOpenOptionsMenu = () => {
        dispatch(onOpenOptionsMenu());
    };

    const handleCloseOptionsMenu = () => {
        if (!isConfirmModalOpen) {
            dispatch(onCloseOptionsMenu());
        }
    };

    const onActionClick = (actionName: string) => {
        if (
            actionName === OptionsMenuActionsName.UPLOAD_GRADES_FROM_EXCEL
                || actionName === OptionsMenuActionsName.UPLOAD_MARK_SCHEME
                || actionName === OptionsMenuActionsName.REPLACE_MARK_SCHEME
                || actionName === OptionsMenuActionsName.MARKS_IMPORT_AS_EXCEL
        ) {
            uploadFile(actionName);
        }
        dispatch(onMenuItemClick(actionName));
    };
    const toggleConfirmModal = (actionName?: string) => {
        dispatch(onToggleConfirmModal(actionName));
    };
    const uploadFile = (actionName: OptionsMenuActionsName) => {
        const input = document.createElement('input');
        input.style.display = 'none';
        input.type = 'file';
        input.onchange = (event: any) => {
            const file = event.target.files[0];
            dispatch(onFileReadyForUpload(file, actionName));
        };
        input.click();
    };
    const cancelItemAction = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        dispatch(onCancelAction());
    };

    const getTranslatedActionName = (action: MenuItemAction): string => {
        const { actionName } = action;
        const translatedActionName = tl(`grading.header.optionsMenu.action.${actionName}`);

        if (actionName === OptionsMenuActionsName.OPEN_QUESTION_WEIGHTS_EDITOR && assessment.isAssessmentPath) {
            return tl('grading.header.optionsMenu.action.editTestWeights');
        }

        if (actionName === OptionsMenuActionsName.DOWNLOAD_AND_PRINT_ALL_SUBMISSIONS_PDF) {
            return `${translatedActionName} (${context.workflowInfo.numberOfSubmissionsForContext})`;
        }

        if (actionName === OptionsMenuActionsName.DOWNLOAD_AND_PRINT_COMMITTEE_SUBMISSIONS_PDF) {
            const currentCommittee = getCommitteeById(selectedCommitteeId);
            const candidateCount = currentCommittee?.candidateCount || context.workflowInfo.numberOfCandidatesForContext;
            return `${translatedActionName} (${candidateCount})`;
        }

        return translatedActionName;
    };

    const isActionDisabled = (
        action: MenuItemAction,
        itemsGroup: string,
        committeeTestAllCommitteesContext: boolean,
    ): boolean => (
        itemsGroup === OptionsMenuSubtitles.WORKFLOW
        && committeeTestAllCommitteesContext
        && action.actionName !== OptionsMenuActionsName.CLOSE_TEST_TO_START_GRADING
        && action.actionName !== OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR
    );

    const shouldConfirmAction = (action: MenuItemAction, subMenuAction?: MenuItemAction): boolean => {
        if (!(action.hasConfirmModal || subMenuAction?.hasConfirmModal)) {
            return false;
        }
        if (isNullOrUndefined(action.notSubmittedCandidatesWarning)) {
            return true;
        }

        const { workflowInfo } = context;
        const { numberOfNotSubmittedCandidates, longestDeliveryDeadlineWithExtraTime } = workflowInfo;
        const longestDeliveryDeadlineMoment = moment.utc(
            longestDeliveryDeadlineWithExtraTime,
            SIMPLE_DATE_FORMAT_STRING_NO_SECONDS,
        );

        if (numberOfNotSubmittedCandidates > 0 && longestDeliveryDeadlineMoment.local().isAfter(moment())) {
            return true;
        }

        return action.actionName === OptionsMenuActionsName.OPEN_WORKFLOW_IN_CONFIRM
        && isGraderThatStillHasNotGradedCandidates();
    };

    const createOnItemClickHandler = (action: MenuItemAction) => (
        event: ClickEvent,
        subMenuAction?: MenuItemAction,
    ) => {
        if (action.openNewTabFromIA || subMenuAction?.openNewTabFromIA) {
            return openNewTabInPlatform(event.value);
        }
        // keep menu open after activating descendent menu items
        event.keepOpen = true;

        if (shouldConfirmAction(action, subMenuAction)) {
            return toggleConfirmModal(event.value);
        }

        return onActionClick(event.value);
    };

    // --- Menu item content ---

    const getEditedLabelContent = (translatedActionName: string, showEditedLabel: boolean) => (
        <React.Fragment>
            {translatedActionName}
            {showEditedLabel ? <Label color="grey">{tl('grading.threshold.text.edited')}</Label> : null}
        </React.Fragment>
    );

    const getCommitteeSelectorContent = (translatedActionName: string, actionName: string) => {
        const onOpenCommitteeSelector = (event: React.SyntheticEvent) => {
            event.stopPropagation();
            onActionClick(actionName);
        };
        return (
            <Message>
                <span>{`${translatedActionName}, `}</span>
                <span className={`${styles.selectCommitteeLink} ${styles.simpleButton}`} onClick={onOpenCommitteeSelector}>
                    {tl('grading.overlay.committeesSelector.title').toString().toLowerCase()}
.
                </span>
            </Message>
        );
    };

    const getProcessingContent = () => (
        <React.Fragment>
            <Loader inline size="mini" active />
            <span className={styles.processStatus}>
                {tl('grading.header.optionsMenu.action.processing')}
            </span>
            <span className={styles.cancelAction} onClick={cancelItemAction}>
                {tl('grading.header.optionsMenu.action.cancel')}
            </span>
        </React.Fragment>
    );

    const getThresholdOrWeightsContent = (action: MenuItemAction, translatedActionName: string) => {
        const isLocked = false;
        const lockedBy: number = null;
        if (isLocked) {
            const evaluatorName = getEvaluatorNameById(lockedBy, selectedCommitteeId);
            return (
                <React.Fragment>
                    {translatedActionName}
                    <LabelSpinner color="red" text={evaluatorName} />
                </React.Fragment>
            );
        }

        if (isNullOrUndefined(assessment.weightThresholdDraftId)) {
            return translatedActionName;
        }

        const isWeightsActionAndHasChanges = action.actionName === OptionsMenuActionsName.OPEN_QUESTION_WEIGHTS_EDITOR
        && assessment.hasQuestionWeights;
        const isThresholdsActionAndHasChanges = action.actionName === OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR
        && assessment.hasThresholds;

        return getEditedLabelContent(translatedActionName, isWeightsActionAndHasChanges || isThresholdsActionAndHasChanges);
    };

    const getMenuItemContent = (
        action: MenuItemAction,
        translatedActionName: string,
        isItemActive: boolean,
        committeeTestAllCommitteesContext: boolean,
    ) => {
        const { actionName } = action;

        if (actionName === OptionsMenuActionsName.OPEN_COMMITTEE_SELECTOR && committeeTestAllCommitteesContext) {
            return getCommitteeSelectorContent(translatedActionName, actionName);
        }

        if (isProcessing && isItemActive) {
            return getProcessingContent();
        }

        if (actionName === OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR
        || actionName === OptionsMenuActionsName.OPEN_QUESTION_WEIGHTS_EDITOR) {
            return getThresholdOrWeightsContent(action, translatedActionName);
        }

        if (actionName === OptionsMenuActionsName.OPEN_QUESTION_ASSIGNMENTS_EDITOR
        && !isNullOrUndefined(assessment.questionAssignmentDraftId)) {
            return getEditedLabelContent(translatedActionName, true);
        }

        if (actionName === OptionsMenuActionsName.OPEN_ANSWER_KEY_CORRECTION_EDITOR
        && !isNullOrUndefined(assessment.questionAnswerKeyCorrectionDraftId)) {
            return getEditedLabelContent(translatedActionName, true);
        }

        return translatedActionName;
    };

    // --- Submenu rendering ---

    const getSubMenuData = (action: MenuItemAction, translatedActionName: string) => {
        if (action.actionName === OptionsMenuActionsName.DOWNLOAD_MARK_SCHEME_SUBMENU) {
            const subMenuLabel = action.actionName.includes('download') && markSchemeFile
                ? `${translatedActionName} ${markSchemeFile.title}${markSchemeFile.extension}`
                : `${translatedActionName} ${tl('grading.header.notFound')}`;
            const subMenuItems = action.nested?.filter((item: MenuItemAction) => !!markSchemeFile === item.visibleWithFile);
            return { subMenuLabel, subMenuItems };
        }
        return { subMenuLabel: translatedActionName, subMenuItems: action.nested };
    };

    const renderSubMenuItem = (
        subMenuItem: MenuItemAction,
        onItemClick: (event: ClickEvent, subMenuAction?: MenuItemAction) => void,
    ) => {
        const { actionName, icon } = subMenuItem;
        const onSubMenuItemClick = (event: ClickEvent) => onItemClick(event, subMenuItem);
        return (
            <MenuItem key={actionName} onClick={onSubMenuItemClick} value={actionName}>
                {tl(`grading.header.optionsMenu.action.${actionName}`)}
                {icon && <Icon name={icon} />}
            </MenuItem>
        );
    };

    const renderSubMenu = (
        action: MenuItemAction,
        translatedActionName: string,
        onItemClick: (event: ClickEvent, subMenuAction?: MenuItemAction) => void,
    ) => {
        const { subMenuLabel, subMenuItems } = getSubMenuData(action, translatedActionName);
        return (
            <SubMenu label={subMenuLabel} key={action.actionName}>
                {subMenuItems?.map((subMenuItem: MenuItemAction) => renderSubMenuItem(subMenuItem, onItemClick))}
            </SubMenu>
        );
    };

    // --- Single menu item ---

    const renderMenuItem = (
        action: MenuItemAction,
        itemsGroup: string,
        committeeTestAllCommitteesContext: boolean,
    ) => {
        const { actionName } = action;
        const translatedActionName = getTranslatedActionName(action);
        const isItemActive = activeActionName === actionName;
        const onItemClick = createOnItemClickHandler(action);
        const disabled = isActionDisabled(action, itemsGroup, committeeTestAllCommitteesContext);

        if (action.nested) {
            return renderSubMenu(action, translatedActionName, onItemClick);
        }

        const menuItemContent = getMenuItemContent(action, translatedActionName, isItemActive, committeeTestAllCommitteesContext);

        return (
            <MenuItem
                key={actionName}
                className={actionName}
                value={actionName}
                checked={isItemActive}
                onClick={onItemClick}
                disabled={disabled}
            >
                {menuItemContent}
            </MenuItem>
        );
    };

    // --- Entry point (same signature/behavior as before) ---

    const renderMenuItemsGroup = (actions: MenuItemAction[], itemsGroup: string) => {
        const committeeTestAllCommitteesContext = assessment.committees && selectedCommitteeId <= 0;

        return actions
            .filter((action: MenuItemAction) => !action.isNotVisible)
            .map((action: MenuItemAction) => renderMenuItem(action, itemsGroup, committeeTestAllCommitteesContext));
    };


    const renderMenuItemsGroupHeader = (itemsGroupHeader: any) => {
        let groupHeader: any = tl(`grading.header.optionsMenu.subtitle.${itemsGroupHeader}`);

        if (itemsGroupHeader === OptionsMenuSubtitles.EXTERNAL_SYSTEM && assessment.externalSystem) {
            groupHeader = assessment.externalSystem;
        }

        if (itemsGroupHeader === OptionsMenuSubtitles.WORKFLOW) {
            if (assessment.committees && selectedCommitteeId <= 0) {
                groupHeader = (
                    <React.Fragment>
                        <span>
                            {tl(`grading.header.optionsMenu.subtitle.${itemsGroupHeader}.single`,
                                {
                                    subtitleName: tl('grading.header.link.openCommitteeSelector')
                                        .toString()
                                        .toUpperCase(),
                                })}
                        </span>
                        <Label
                            className={styles.limitedLabel}
                            size="small"
                            horizontal
                            color="orange"
                        >
                            {tl('grading.header.optionsMenu.subtitle.limitedWorkflow')}
                        </Label>
                    </React.Fragment>
                );
            } else if (getNumberOfEvaluators(selectedCommitteeId) === 1) {
                const evaluatorNames = getEvaluatorsNames(selectedCommitteeId);
                // if the evaluatorNames array is empty, that means the current user is the grader (his name is excluded on the backend
                const evaluatorName = evaluatorNames?.length > 0
                    ? evaluatorNames[0]
                    : `${session.user.firstName} ${session.user.lastName}`;
                groupHeader = tl(`grading.header.optionsMenu.subtitle.${itemsGroupHeader}.single`,
                    { subtitleName: evaluatorName, interpolation: { escapeValue: false } });
            } else {
                const committeeName = getCommitteeNameByID(selectedCommitteeId);
                groupHeader = tl(`grading.header.optionsMenu.subtitle.${itemsGroupHeader}.single`,
                    { subtitleName: committeeName, interpolation: { escapeValue: false } });
            }
        }

        return (
            <MenuHeader>
                {groupHeader}
            </MenuHeader>
        );
    };

    const renderMenuItems = () => {
        const menuItems = getOptionsMenuItems();
        return menuItems.map((itemsGroup: MenuItemsGroup, index: number) => {
            const { groupHeader, actions } = itemsGroup;
            const actionsInMenuGroup = renderMenuItemsGroup(actions, groupHeader);

            return (
                actionsInMenuGroup.length > 0 && (
                    <MenuGroup key={itemsGroup.groupHeader}>
                        {index > 0 && <MenuDivider />}
                        {renderMenuItemsGroupHeader(groupHeader)}
                        {actionsInMenuGroup}
                    </MenuGroup>
                )
            );
        });
    };

    const renderConfirmModal = () => {
        const menuItems = getOptionsMenuItems();
        const activeAction = menuItems
            .reduce((acc: MenuItemAction[], val: MenuItemsGroup) => acc.concat(val.actions), [])
            .find((action: MenuItemAction) => action.actionName === activeActionName);
        const activeGroup = menuItems
            .find((group: MenuItemsGroup) => group.actions.some((action: MenuItemAction) => action.actionName === activeActionName));
        const isModerationAction = activeGroup?.groupHeader === OptionsMenuSubtitles.MODERATION;
        const onConfirmClick = () => {
            if (activeAction?.confirmModalHasCommentTextarea) {
                let moderationStatus: ModerationStatus | null = null;
                if (activeActionName === OptionsMenuActionsName.SET_MODERATION_STATUS_APPROVE) {
                    moderationStatus = ModerationStatus.SATISFACTORY;
                } else if (activeActionName === OptionsMenuActionsName.SET_MODERATION_STATUS_DO_NOT_APPROVE) {
                    moderationStatus = ModerationStatus.UNSATISFACTORY;
                }
                if (moderationStatus !== null) {
                    dispatch(onModerationActionClick(
                        ModerationPrimaryActions.SET_MODERATION_STATUS,
                        moderationStatus,
                        moderationComment || undefined,
                    ));
                }
            }
            onActionClick(activeActionName);
            setModerationComment('');
        };
        const onCancelClick = () => {
            setModerationComment('');
            toggleConfirmModal();
        };
        let contentCaption = tl(`grading.optionsMenu.action.confirm.content.${activeActionName}`);
        let contentHeader = null;
        let contentInstruction = null;
        let cancelButtonCaption = tl('grading.optionsMenu.modal.button.cancel');
        let confirmButtonCaption: any = tl('grading.optionsMenu.modal.button.confirm');
        let captionParams = {};
        if (activeAction?.notSubmittedCandidatesWarning) {
            const { workflowInfo } = context;
            const { numberOfNotSubmittedCandidates, longestDeliveryDeadlineWithExtraTime } = workflowInfo;
            const longestDeliveryDeadlineMoment = moment.utc(longestDeliveryDeadlineWithExtraTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
            captionParams = {
                ...captionParams,
                latestIddOrExtraTimeDate: longestDeliveryDeadlineMoment.local().format('L LT'),
                // disable escaping the date
                interpolation: { escapeValue: false },
            };
            if (numberOfNotSubmittedCandidates > 0) {
                contentCaption = tl('grading.optionsMenu.action.confirm.content.incompleteCandidatesList', captionParams);
                cancelButtonCaption = tl('grading.optionsMenu.modal.button.comeBackLater');
                confirmButtonCaption = tl('grading.optionsMenu.modal.button.continueWithIncompleteList');
            }
        }
        if (activeAction?.confirmModalInstructions?.length > 0) {
            contentHeader = tl(`grading.optionsMenu.action.confirm.header.${activeActionName}`);
            contentInstruction = (
                <Message className="instructions">
                    <Header as="h5">{tl('grading.optionsMenu.action.confirm.bestPractices')}</Header>
                    <List as="ol">
                        {activeAction.confirmModalInstructions.map((item) => <ListItem as="li" key={item}>{item}</ListItem>)}
                    </List>
                    <a href={supportArticleForExplanations} target="_blank" className={styles.link}>
                        {tl('grading.uploadGradeAndExplanation.viewDocumentation')}
                    </a>
                    <span>{tl('grading.uploadGradeAndExplanation.moreDetails')}</span>
                </Message>
            );
        }
        if (activeAction?.confirmModalHasCommentTextarea) {
            contentHeader = tl(`grading.optionsMenu.action.confirm.header.${activeActionName}`);
            const confirmBtnLabel = tl(activeAction.confirmModalConfirmButtonLabel);
            if (activeActionName === OptionsMenuActionsName.SET_MODERATION_STATUS_DO_NOT_APPROVE) {
                confirmButtonCaption = { content: confirmBtnLabel, className: styles.doNotApproveModalButton };
            } else {
                confirmButtonCaption = confirmBtnLabel;
            }
            contentInstruction = (
                <div className={styles.confirmModalWithComment}>
                    <p>{contentCaption}</p>
                    <Form>
                        <Form.Field>
                            <label htmlFor="moderation-comment">
                                {tl('grading.optionsMenu.action.confirm.label.comments')}
                            </label>
                            <TextArea
                                id="moderation-comment"
                                rows={4}
                                value={moderationComment}
                                onChange={(_, data) => setModerationComment(data.value as string)}
                            />
                        </Form.Field>
                    </Form>
                </div>
            );
        }
        return (
            <Confirm
                className={isModerationAction ? 'moderationConfirmModal' : undefined}
                header={contentHeader}
                content={contentInstruction || contentCaption}
                open={isConfirmModalOpen}
                closeIcon={!!activeAction?.confirmModalShowCloseIcon}
                cancelButton={cancelButtonCaption}
                confirmButton={confirmButtonCaption}
                onCancel={onCancelClick}
                onConfirm={onConfirmClick}
            />
        );
    };
    return (
        <React.Fragment>
            <MenuButton
                ref={optionsMenuRef}
                onClick={handleOnOpenOptionsMenu}
                className="ui button dropdown basic"
            >
                {tl('grading.header.options')}
                <Icon name="dropdown" />
            </MenuButton>
            <ControlledMenu
                anchorRef={optionsMenuRef}
                onClose={handleCloseOptionsMenu}
                overflow="auto"
                position="initial"
                reposition="initial"
                direction="bottom"
                align="center"
                offsetY={10}
                state={isMenuOpen ? 'open' : 'closed'}
            >
                {renderMenuItems()}
            </ControlledMenu>
            {renderConfirmModal()}
        </React.Fragment>
    );
});


export default OptionsMenuButton;
